<template>
  <div class="sonar-answers">
    <div v-if="!answerTypeText" class="sonar-answers__selection-wrapper">
      <div
        v-for="answer in answers"
        :key="`answer-id-${answerId(answer)}`"
        :class="[
          'sonar-answers__selection',
          isCorrectAnswer(answer) && 'sonar-answers__correct-answer',
          questionTimesUp && 'sonar-answers__selection--times-up',
        ]"
      >
        <label
          :class="[
            'sonar-answers__select',
            questionTimesUp && 'sonar-answers__select--times-up',
          ]"
        >
          <span
            v-if="!isAssessmentOutOfTime"
            :class="[
              'sonar-answer__input',
              currentQuestion.widget &&
                `sonar-answer__input--${currentQuestion.widget}`,
              isSelected(answer) && 'sonar-answer__input--selected',
              disableAnswers && 'sonar-answer__input--disabled',
            ]"
          >
            <input
              v-model="currentSelection"
              :class="[isSelected(answer) && 'sonar-answer--checked']"
              :type="currentQuestion.widget || 'checkbox'"
              :name="`${answerId(answer)}-checkbox`"
              :value="answerId(answer)"
              :disabled="disableAnswers"
            />
            <span
              :class="
                `sonar-answer__control sonar-answer__control--${currentQuestion.widget ||
                  'checkbox'}`
              "
            >
              <span
                v-if="currentQuestion.widget === 'radio'"
                class="sonar-answer__radio-dot"
              />
              <CheckboxTick v-else />
            </span>
          </span>
          <div class="sonar-answers__answer">
            <span v-html="answerContent(answer)" />
          </div>
        </label>
      </div>
    </div>
    <SonarAnswerTextbox
      v-else
      v-bind="{
        currentQuestion,
        correctTextAnswer,
        displayCorrectAnswers,
      }"
      :isDisabled="isAssessmentOutOfTime || isLastQuestionAnswered"
      @update-selection="updateSelectedAnswers"
    />
    <div
      v-if="!isLoading"
      :class="[
        'sonar-answers__btn-wrapper',
        isLabQuestion && 'sonar-answers__btn-wrapper--lab',
        isAssessmentOutOfTime && 'sonar-answers__btn-wrapper--times-up',
      ]"
    >
      <div v-if="isAssessmentOutOfTime" class="sonar-answers__warning-wrapper">
        <InfoIcon class="sonar-answers__warning-icon" />
        <div class="sonar-answers__warning-label">
          Sorry, you ran out of time.
        </div>
      </div>
      <div
        class="sonar-answers__action-buttons sonar-answers__action-buttons__with-time"
      >
        <button
          v-if="!isLastQuestion"
          class="
            btn
            btn--primary
            btn--primary--with-icon
            btn--primary--with-icon--right
            btn--default-icon-color
            sonar-answers__btn sonar-answers__btn__next
          "
          :disabled="submitAnswerDisabled || isLastQuestionAnswered"
          @click="answerQuestionAndContinue()"
        >
          {{ nextButtonLabel }}
          <ArrowRightIcon />
        </button>
        <button
          v-if="isLastQuestion"
          class="
            btn
            btn--primary
            btn--primary--with-icon
            btn--primary--with-icon--right
            sonar-answers__btn sonar-answers__btn__next
            btn--default-icon-color
          "
          :disabled="submitAnswerDisabled"
          @click="submitExam()"
        >
          Submit assessment
          <ArrowRightIcon class="sonar-answers__btn__next--icon" />
        </button>
      </div>
    </div>
    <div
      v-else
      class="sonar-answers__btn-wrapper
        sonar-answers__saving-wrapper
      "
    >
      <div class="sonar-answers__saving">
        <span class="dot-flashing" />Saving
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { ArrowRightIcon, InfoIcon } from 'vue-feather-icons'
import CheckboxTick from '@/assets/images/certification/checkbox-tick.svg'
import CONSTANTS from '@/constants'
import {
  setValue,
  getSonarAssessmentValue,
} from '@/sonar/helpers/sessionStorageHandler'
import SonarAnswerTextbox from './SonarAnswerTextbox.vue'

export default {
  name: 'SonarAnswers',
  components: {
    ArrowRightIcon,
    CheckboxTick: { ...CheckboxTick, name: 'CheckboxTick' },
    SonarAnswerTextbox,
    InfoIcon,
  },
  props: {
    currentQuestion: {
      type: Object,
      required: true,
    },
    lastQuestion: {
      type: Object,
      required: true,
    },
    isLastQuestion: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      currentSelection: [],
      isLoading: false,
      isSubmittingQuestion: false,
      submitAnswerError: false,
    }
  },
  watch: {
    currentSelection() {
      if (this.currentQuestion.answers) {
        this.currentQuestion.answers.map(a => {
          a.is_selected = this.currentSelection?.includes(this.answerId(a))
        })
      } else {
        return [this.currentQuestion.question_id]
      }
      setValue({ lastSubmittedAnswers: this.currentSelection })
    },
  },
  computed: {
    ...mapGetters({
      isAssessmentOutOfTime: 'sonar/isAssessmentOutOfTime',
      isLastQuestionAnswered: 'sonar/isLastQuestionAnswered',
      assessmentAttempt: 'sonar/assessmentAttempt',
      isAdminOrContentCreator: 'auth/isAdminOrContentCreator',
      displayCorrectAnswers: 'sonar/displayCorrectAnswers',
      currentLabSession: 'labs/currentLabSession',
    }),
    answers() {
      const { answers } = this.currentQuestion
      return answers
    },
    answerTypeText() {
      return (
        this.currentQuestion.question_type ===
        CONSTANTS.SONAR.QUESTION_TYPES.INPUT
      )
    },
    nextButtonLabel() {
      if (this.isAssessmentOutOfTime) return 'Next question'
      return 'Submit answer'
    },
    questionTimesUp() {
      return (
        this.isAssessmentOutOfTime ||
        this.isLoading ||
        this.isLastQuestionAnswered
      )
    },
    disableAnswers() {
      return (
        this.isLastQuestionAnswered ||
        this.isLoading ||
        this.isSubmittingQuestion ||
        this.submitAnswerError
      )
    },
    submitAnswerDisabled() {
      return !this.currentSelection.length && !this.isAssessmentOutOfTime
    },
    isLabQuestion() {
      return this.currentQuestion.hasLab
    },
    correctTextAnswer() {
      if (!this.answerTypeText || !this.isAdminOrContentCreator) return ''
      return this.currentQuestion?.correct_answer
    },
  },
  mounted() {
    this.currentSelection =
      getSonarAssessmentValue('lastSubmittedAnswers') || []
  },
  methods: {
    updateSelectedAnswers(value) {
      this.currentSelection = [value]
    },
    answerContent(answer) {
      return answer.content_html
    },
    isSelected(answer) {
      return this.currentSelection.includes(answer.id)
    },
    isCorrectAnswer(answer) {
      if (!this.isAdminOrContentCreator || !answer?.is_correct) return
      return this.displayCorrectAnswers && answer?.is_correct
    },
    nextQuestion() {
      this.$store.dispatch('sonar/nextQuestion')
    },
    async answerQuestionAndContinue() {
      if (!this.isAssessmentOutOfTime) await this.answerQuestion()
      if (!this.isLastQuestion && !this.submitAnswerError) this.nextQuestion()
    },
    async answerQuestion(isLastAnswer = false) {
      try {
        if (this.isSubmittingQuestion) return
        this.isSubmittingQuestion = true
        if (!this.isAssessmentOutOfTime) this.isLoading = true
        this.$parent.$parent.$emit('is-answering-question', true)
        const selected_answer_ids =
          this.currentQuestion.widget === 'radio'
            ? [this.currentSelection]
            : this.currentSelection

        const payload = {
          question_id: this.currentQuestion.id,
          assessment_attempt_id: this.assessmentAttempt.id,
          submitted_answer: {
            selected_answer_ids:
              this.isAssessmentOutOfTime ||
              this.currentSelection ===
                CONSTANTS.SONAR.ASSESSMENT.ANSWER.DONT_KNOW
                ? []
                : selected_answer_ids,
            input_answer:
              this.currentSelection.length &&
              this.answerTypeText &&
              !this.isAssessmentOutOfTime
                ? this.currentSelection[0]
                : '',
          },
        }

        if (this.isLabQuestion) {
          payload.lab_session_id = this.currentLabSession?.id
        }
        await this.$store.dispatch('sonar/submitAssessmentAnswer', payload)
        if (!isLastAnswer) this.currentSelection = []
        const nextPosition = this.currentQuestion.position + 1
        const questionsAmount = this.assessmentAttempt.questions.length
        const nextQuestionPosition =
          nextPosition < questionsAmount ? nextPosition : questionsAmount
        setValue({ position: nextQuestionPosition, lastSubmittedAnswers: [] })
      } catch (e) {
        this.submitAnswerError = true
      } finally {
        this.isSubmittingQuestion = false
        this.isLoading = false
      }
    },
    async submitExam() {
      const isLastAnswer = true
      if (!this.isAssessmentOutOfTime && !this.isLastQuestionAnswered)
        await this.answerQuestion(isLastAnswer)
      this.$store.dispatch('sonar/setLastQuestionAnswered', true)
      this.$parent.$parent.$emit('submit-exam')
    },
    answerId(answer) {
      return answer.id
    },
  },
}
</script>

<style lang="scss" scoped>
.sonar-answers {
  padding: 0 20px 20px;
  color: #cecece;
  @include desktop {
    margin: 0;
    padding: 0 20px 15px 20px;
  }
  &__selection {
    display: flex;
    margin: 0 0 5px 0;
    border: 1px solid var(--tundora-gray);
    border-radius: 10px;
    min-height: 46px;
    cursor: pointer;
    &--times-up {
      cursor: inherit;
    }
    &:hover:not(.sonar-answers__selection--times-up) {
      background: var(--glass-extra-light);
      outline: solid !important;
      outline-width: 2px !important;
      outline-color: var(--emperor-gray) !important;
      .sonar-answer__input {
        border-color: var(--white);
      }
    }
  }
  &__select {
    display: flex;
    gap: 18px;
    font-size: rem(20);
    line-height: rem(26);
    cursor: pointer;
    position: relative;
    padding: 12px 17px;
    width: 100%;
    align-items: center;
    &--times-up {
      cursor: inherit;
    }
  }
  &__correct-answer {
    background: var(--positive-light-20);
    outline: solid !important;
    outline-color: var(--positive-light) !important;
    &:hover:not(.sonar-answers__selection--times-up) {
      background: var(--positive-light-15);
      outline: solid;
      outline-width: 3px !important;
      outline-color: var(--positive-light) !important;
    }
  }
  &__action-buttons {
    display: flex;
    &__wrapper {
      background: var(--glass-mid-gray);
      width: 60px;
      border-radius: 30px;
      height: 60px;
      display: flex;
      justify-content: center;
      align-items: center;
      cursor: pointer;
      transition: 0.2s all ease;
      &--active {
        background: transparent
          linear-gradient(180deg, #ef7d22 0%, #ee3b00 100%) 0% 0% no-repeat
          padding-box;
        svg {
          path {
            stroke: var(--white);
            fill: var(--white);
          }
        }
      }
    }

    &__with-time {
      display: flex;
      flex-direction: column-reverse;
      align-items: flex-end;
      justify-content: flex-end;

      @include tablet {
        flex-direction: column-reverse;
      }
      gap: 26px;
    }
  }
  &__icon {
    position: absolute;
    top: -1px;
    right: -1px;
    &--filled {
      fill: var(--green);
    }
  }
  &__answer {
    width: 100%;
    font-size: rem(20);
    ::v-deep p:last-child {
      width: 100%;
      margin: 0;
      overflow-wrap: anywhere;
      @include desktop {
        overflow-wrap: anywhere;
      }
    }
    @include desktop {
      font-size: 18px;
    }
    pre {
      margin: 0;
    }
    span pre code {
      white-space: pre-line;
    }
  }
  &__btn {
    cursor: pointer;
    &-wrapper {
      display: flex;
      justify-content: flex-end;
      margin-top: 30px;
      &--lab {
        flex-direction: column;
        gap: 45px;
        margin-top: 45px;
      }
      &--times-up {
        justify-content: space-between;
      }
    }
    &__next {
      order: 1;
    }
  }
  &__warning-wrapper {
    display: flex;
    align-items: center;
    gap: 15px;
    color: var(--carnation-sonar);
  }
  &__warning-label {
    font-size: rem(16);
    line-height: rem(24);
  }
  &__saving {
    display: flex;
    align-items: center;
    width: 100px;
    justify-content: space-around;
    margin-right: 10px;
    padding: 19px 0 20px 0;
    &-wrapper {
      justify-content: flex-end;
    }
  }
  &__saved-time {
    width: 100%;
    text-align: right;
    font-size: 0.75rem;
    color: var(--silver-chalice);
    margin-top: 20px;
    padding-right: 10px;
    order: 0;
    position: relative;
    @include tablet {
      margin: 26px 0 30px 0;
      font-size: 0.75rem;
      padding-right: 0;
    }
    @include lg-desktop {
      font-size: 0.85rem;
    }
    &--highlighted {
      margin-right: 5px;
      color: var(--white);
      animation-delay: 2s;
      animation-duration: 2s;
      animation-name: fadeOut;
      animation-fill-mode: forwards;
      svg {
        margin-right: 5px;
        path {
          stroke: var(--green);
        }
      }
      @keyframes fadeOut {
        0% {
          opacity: 1;
        }
        100% {
          opacity: 0;
        }
      }
    }
    &--last-question {
      @include tablet {
        width: 100%;
        .sonar-answers__saved-time--highlighted {
          right: 10px;
        }
      }
    }
  }
  img {
    max-width: 450px;
  }
}
</style>
